import { useCallback, useEffect, useRef } from 'react';
import { useAuthStore } from '../stores/authStore';
import { api } from '../services/api';

// SECURITY: 5-minute inactivity timeout
const INACTIVITY_TIMEOUT = 5 * 60 * 1000;

export function useAuth() {
  const store = useAuthStore();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Initialize auth state on mount
  useEffect(() => {
    // Set auth token if we have one
    if (store.accessToken) {
      api.setAuthToken(store.accessToken);
    }
  }, [store.accessToken]);

  // SECURITY: Inactivity timeout - auto logout after 5 minutes
  useEffect(() => {
    if (!store.isAuthenticated) return;

    const resetTimer = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        console.log('Inactivity timeout - logging out');
        store.logout();
        api.setAuthToken(null);
        window.location.reload(); // Force page reload to show login
      }, INACTIVITY_TIMEOUT);
    };

    // PC events
    window.addEventListener('mousemove', resetTimer);
    window.addEventListener('keypress', resetTimer);
    window.addEventListener('keydown', resetTimer);

    // Tablet/mobile events
    window.addEventListener('touchstart', resetTimer);
    window.addEventListener('scroll', resetTimer);
    window.addEventListener('click', resetTimer);

    // Start initial timer
    resetTimer();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      window.removeEventListener('mousemove', resetTimer);
      window.removeEventListener('keypress', resetTimer);
      window.removeEventListener('keydown', resetTimer);
      window.removeEventListener('touchstart', resetTimer);
      window.removeEventListener('scroll', resetTimer);
      window.removeEventListener('click', resetTimer);
    };
  }, [store.isAuthenticated, store.logout]);

  // Check if WebAuthn is supported - wrapped to be Safari-safe
  // NOTE: Safari can throw "The operation is insecure" in certain contexts
  const isWebAuthnSupported = useCallback(() => {
    // Default to false - only return true if we can safely check
    try {
      if (typeof window === 'undefined') return false;
      if (typeof window.PublicKeyCredential === 'undefined') return false;
      if (typeof window.PublicKeyCredential !== 'function') return false;
      return true;
    } catch {
      // Any error means WebAuthn is not safely available
      return false;
    }
  }, []);

  // Check if platform authenticator (Face ID, Touch ID, Windows Hello) is available
  // NOTE: Safari may throw "The operation is insecure" for WebAuthn APIs
  // in certain contexts, so we wrap this carefully
  const isPlatformAuthenticatorAvailable = useCallback(async () => {
    if (!isWebAuthnSupported()) return false;
    try {
      // Safari has strict requirements - this can throw in certain contexts
      if (typeof PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable !== 'function') {
        return false;
      }
      return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    } catch (err) {
      // Safari may throw "The operation is insecure" - handle gracefully
      console.warn('Platform authenticator check failed (Safari security restriction):', err);
      return false;
    }
  }, [isWebAuthnSupported]);

  // Register a new passkey
  const register = useCallback(async (username: string, displayName?: string, deviceName?: string) => {
    store.setLoading(true);
    store.setError(null);

    try {
      // Get registration options from server
      const options = await api.getRegisterOptions(username, displayName);

      // Create credential using WebAuthn API
      const credential = await navigator.credentials.create({
        publicKey: api.prepareRegistrationOptions(options),
      }) as PublicKeyCredential;

      if (!credential) {
        throw new Error('Failed to create credential');
      }

      // Verify with server
      const result = await api.verifyRegistration(options.challengeId, credential, deviceName);

      // Store auth state
      store.setAuth({
        user: result.user,
        accessToken: result.tokens.access_token,
        refreshToken: result.tokens.refresh_token,
        authType: 'passkey',
      });

      api.setAuthToken(result.tokens.access_token);

      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Registration failed';
      store.setError(message);
      throw error;
    } finally {
      store.setLoading(false);
    }
  }, [store]);

  // Login with passkey
  const login = useCallback(async (username?: string) => {
    store.setLoading(true);
    store.setError(null);

    try {
      // Get authentication options from server
      const options = await api.getLoginOptions(username);

      // Get credential using WebAuthn API
      const credential = await navigator.credentials.get({
        publicKey: api.prepareAuthenticationOptions(options),
      }) as PublicKeyCredential;

      if (!credential) {
        throw new Error('Failed to get credential');
      }

      // Verify with server
      const result = await api.verifyLogin(options.challengeId, credential);

      // Store auth state
      store.setAuth({
        user: result.user,
        accessToken: result.tokens.access_token,
        refreshToken: result.tokens.refresh_token,
        authType: 'passkey',
      });

      api.setAuthToken(result.tokens.access_token);

      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Login failed';
      store.setError(message);
      throw error;
    } finally {
      store.setLoading(false);
    }
  }, [store]);

  // Login with recovery code
  const loginWithRecoveryCode = useCallback(async (username: string, code: string) => {
    store.setLoading(true);
    store.setError(null);

    try {
      const result = await api.verifyRecoveryCode(username, code);

      store.setAuth({
        user: { id: '', username, display_name: username },
        accessToken: result.tokens.access_token,
        refreshToken: result.tokens.refresh_token,
        authType: 'passkey',
      });

      api.setAuthToken(result.tokens.access_token);
      store.setRecoveryCodesRemaining(result.recovery_codes_remaining);

      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Invalid recovery code';
      store.setError(message);
      throw error;
    } finally {
      store.setLoading(false);
    }
  }, [store]);

  // Logout
  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      // Ignore logout errors
    } finally {
      store.logout();
      api.setAuthToken(null);
    }
  }, [store]);

  // Refresh token
  const refreshToken = useCallback(async () => {
    if (!store.refreshToken) return false;

    try {
      const result = await api.refreshToken(store.refreshToken);
      store.setAuth({
        user: store.user!,
        accessToken: result.access_token,
        refreshToken: result.refresh_token,
        authType: store.authType as 'passkey' | 'api_key',
      });
      api.setAuthToken(result.access_token);
      return true;
    } catch {
      store.logout();
      api.setAuthToken(null);
      return false;
    }
  }, [store]);

  // Re-authenticate for sensitive actions
  const reauth = useCallback(async (): Promise<string | null> => {
    try {
      const options = await api.getReauthOptions();

      // Dev mode - no reauth needed
      if ((options as any).skip_reauth) {
        return 'dev-mode-token';
      }

      const credential = await navigator.credentials.get({
        publicKey: api.prepareAuthenticationOptions(options),
      }) as PublicKeyCredential;

      if (!credential) {
        throw new Error('Re-authentication cancelled');
      }

      const result = await api.verifyReauth(options.challengeId, credential);
      return result.reauth_token;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Re-authentication failed';
      store.setError(message);
      return null;
    }
  }, [store]);

  // Check current auth status
  const checkAuthStatus = useCallback(async () => {
    try {
      const result = await api.getCurrentUser();

      if (result.dev_mode) {
        store.setDevMode();
        return { authenticated: false, devMode: true };
      }

      if (result.authenticated && result.user) {
        return { authenticated: true, user: result.user };
      }

      return { authenticated: false };
    } catch {
      return { authenticated: false };
    }
  }, [store]);

  // Load passkeys
  const loadPasskeys = useCallback(async () => {
    try {
      const passkeys = await api.listPasskeys();
      store.setPasskeys(passkeys);
      return passkeys;
    } catch {
      return [];
    }
  }, [store]);

  // Delete a passkey
  const deletePasskey = useCallback(async (credentialId: string) => {
    try {
      await api.deletePasskey(credentialId);
      await loadPasskeys();
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete passkey';
      store.setError(message);
      return false;
    }
  }, [store, loadPasskeys]);

  // Generate recovery codes
  const generateRecoveryCodes = useCallback(async () => {
    try {
      const result = await api.generateRecoveryCodes();
      store.setRecoveryCodesRemaining(result.count);
      return result.codes;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to generate recovery codes';
      store.setError(message);
      return null;
    }
  }, [store]);

  // Get recovery code count
  const getRecoveryCodeCount = useCallback(async () => {
    try {
      const result = await api.getRecoveryCodeCount();
      store.setRecoveryCodesRemaining(result.count);
      return result.count;
    } catch {
      return 0;
    }
  }, [store]);

  return {
    // State
    isAuthenticated: store.isAuthenticated,
    user: store.user,
    isLoading: store.isLoading,
    error: store.error,
    authType: store.authType,
    passkeys: store.passkeys,
    recoveryCodesRemaining: store.recoveryCodesRemaining,

    // UI state
    showLogin: store.showLogin,
    showRegister: store.showRegister,
    setShowLogin: store.setShowLogin,
    setShowRegister: store.setShowRegister,
    setError: store.setError,

    // Actions
    isWebAuthnSupported,
    isPlatformAuthenticatorAvailable,
    register,
    login,
    loginWithRecoveryCode,
    logout,
    refreshToken,
    reauth,
    checkAuthStatus,
    loadPasskeys,
    deletePasskey,
    generateRecoveryCodes,
    getRecoveryCodeCount,
  };
}
